'use client';

import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/lib/auth/context';
import { DEFAULT_COUNTRY, formatPhoneE164, getMaxPhoneLength, validatePhone } from '@/lib/phone';
import type { Country } from '@/lib/phone';
import CountryCodeSelector from '@/components/CountryCodeSelector';
import LoadingSpinner from '@/components/LoadingSpinner';
import { TermsMarketingConsentLabel } from '@/components/auth/TermsMarketingConsentLabel';
import { TERMS_ACCEPTANCE_REQUIRED_MESSAGE } from '@/lib/marketing/consent-copy';

type WhatsAppStep = 'phone' | 'otp';

function initialErrorMessage(code: string | null, email: string | null): string {
  if (code === 'not-registered') {
    const who = email ? `${email}` : 'That email';
    return `${who} could not be signed in. Please try WhatsApp OTP or Google.`;
  }
  if (code === 'oauth_failed') return 'Sign-in failed. Please try again.';
  if (code === 'oauth_cancelled') return 'Sign-in was cancelled.';
  if (code === 'terms_required') return TERMS_ACCEPTANCE_REQUIRED_MESSAGE;
  if (code === 'workspace_google_required') {
    return 'Google sign-in is only available for work email domains. Use WhatsApp OTP for Gmail or other personal email accounts.';
  }
  return '';
}

function safeInternalRedirect(value: string): string {
  if (value.startsWith('/') && !value.startsWith('//')) return value;
  return '/dashboard';
}

export default function SignInPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      }
    >
      <SignInForm />
    </Suspense>
  );
}

function SignInForm() {
  const searchParams = useSearchParams();
  const { sendWhatsAppOtp, verifyWhatsAppOtp, signInWithGoogle } = useAuth();

  const redirectTo = searchParams.get('redirect') || searchParams.get('next') || '/dashboard';

  // Session recordings (2026-07-22): a visitor who just clicked "Track this
  // case free" was greeted with generic sign-IN language and lost the thread.
  // When the redirect carries a track-case intent, speak to it — and show the
  // case they are claiming so the momentum survives the OTP step.
  const trackIntent = useMemo(() => {
    if (!redirectTo.startsWith('/track-case')) return null;
    try {
      const url = new URL(redirectTo, 'https://indiacasestatus.com');
      const petitioner = url.searchParams.get('petitioner') || '';
      const respondent = url.searchParams.get('respondent') || '';
      const caseRef = [url.searchParams.get('type'), url.searchParams.get('number'), url.searchParams.get('case_year')]
        .filter(Boolean)
        .join('/');
      const parties = petitioner && respondent
        ? `${petitioner} vs ${respondent}`
        : petitioner || respondent;
      return { parties, caseRef };
    } catch {
      return { parties: '', caseRef: '' };
    }
  }, [redirectTo]);

  const [selectedCountry, setSelectedCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [whatsAppStep, setWhatsAppStep] = useState<WhatsAppStep>('phone');
  const [phoneSubmitting, setPhoneSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [termsAccepted, setTermsAccepted] = useState(true);
  const termsAcceptedRef = useRef(true);

  const [googleSubmitting, setGoogleSubmitting] = useState(false);
  const [error, setError] = useState(() =>
    initialErrorMessage(searchParams.get('error'), searchParams.get('email'))
  );

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setTimeout(() => setResendCooldown((current) => current - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const maxLen = getMaxPhoneLength(selectedCountry);
  const formattedPhone = formatPhoneE164(phone, selectedCountry.dialCode);
  const phoneDisplay = `+${selectedCountry.dialCode} ${phone}`;

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!termsAcceptedRef.current) {
      setError(TERMS_ACCEPTANCE_REQUIRED_MESSAGE);
      return;
    }

    const validation = validatePhone(phone, selectedCountry.code);
    if (!validation.isValid) {
      setError(validation.error || 'Please enter a valid WhatsApp number.');
      return;
    }

    if (!formattedPhone) {
      setError('Please enter a valid WhatsApp number.');
      return;
    }

    setPhoneSubmitting(true);
    try {
      const { error: sendError } = await sendWhatsAppOtp(formattedPhone, termsAcceptedRef.current);
      if (sendError) {
        setError(sendError.message || 'Could not send WhatsApp OTP. Please try again.');
        return;
      }
      setWhatsAppStep('otp');
      setResendCooldown(60);
    } catch {
      setError('Unexpected error sending WhatsApp OTP.');
    } finally {
      setPhoneSubmitting(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (otp.length !== 6) {
      setError('Enter the 6-digit code sent on WhatsApp.');
      return;
    }

    if (!formattedPhone) {
      setError('Please enter a valid WhatsApp number.');
      return;
    }

    setPhoneSubmitting(true);
    try {
      const { error: verifyError } = await verifyWhatsAppOtp(
        formattedPhone,
        otp,
        undefined,
        undefined,
        termsAcceptedRef.current,
      );
      if (verifyError) {
        setError(verifyError.message || 'Invalid OTP. Please try again.');
        return;
      }

      const autotrack = searchParams.get('autotrack');
      const shareToken = searchParams.get('share_token');
      if (autotrack === 'true' && shareToken) {
        try {
          await fetch('/api/cases/autotrack', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ share_token: shareToken }),
          });
        } catch {
          // Non-fatal; the user can still continue into setup/dashboard.
        }
      }

      window.location.assign(safeInternalRedirect(autotrack === 'true' ? '/dashboard' : redirectTo));
    } catch {
      setError('Unexpected error verifying WhatsApp OTP.');
    } finally {
      setPhoneSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0 || phoneSubmitting) return;
    setError('');

    if (!termsAcceptedRef.current) {
      setError(TERMS_ACCEPTANCE_REQUIRED_MESSAGE);
      return;
    }

    if (!formattedPhone) {
      setError('Please enter a valid WhatsApp number.');
      return;
    }

    setPhoneSubmitting(true);
    try {
      const { error: sendError } = await sendWhatsAppOtp(formattedPhone, termsAcceptedRef.current);
      if (sendError) {
        setError(sendError.message || 'Could not resend OTP. Please try again.');
        return;
      }
      setResendCooldown(60);
    } catch {
      setError('Unexpected error resending WhatsApp OTP.');
    } finally {
      setPhoneSubmitting(false);
    }
  };

  const handleBackToPhone = () => {
    setWhatsAppStep('phone');
    setOtp('');
    setError('');
  };

  const handleGoogleSignIn = async () => {
    setError('');
    if (!termsAcceptedRef.current) {
      setError(TERMS_ACCEPTANCE_REQUIRED_MESSAGE);
      return;
    }
    setGoogleSubmitting(true);
    try {
      const { error: oauthErr } = await signInWithGoogle(
        redirectTo,
        undefined,
        undefined,
        termsAcceptedRef.current,
      );
      if (oauthErr) {
        setError(oauthErr.message);
        setGoogleSubmitting(false);
      }
    } catch {
      setError('Unexpected error starting Google sign-in.');
      setGoogleSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 px-4 sm:py-12 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6 sm:space-y-8">
        <div className="text-center">
          {trackIntent ? (
            <>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
                Claim your free account to track your case
              </h1>
              <p className="mt-3 text-gray-600 text-sm sm:text-base leading-relaxed">
                Verify your WhatsApp number with a 6-digit code — your case is saved to your account automatically, and updates arrive on the same number. No password needed.
              </p>
              {(trackIntent.parties || trackIntent.caseRef) && (
                <div className="mt-4 inline-flex max-w-full items-center gap-2 rounded-full border border-primary-100 bg-primary-50 px-4 py-2 text-sm font-medium text-primary-800">
                  <svg aria-hidden="true" className="h-4 w-4 shrink-0 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="truncate">
                    Tracking: {trackIntent.parties || trackIntent.caseRef}
                  </span>
                </div>
              )}
            </>
          ) : (
            <>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">Sign in to India Case Status</h1>
              <p className="mt-3 text-gray-600 text-sm sm:text-base leading-relaxed">
                Use WhatsApp OTP first. Work email users can continue with Google and open the demo workspace.
              </p>
            </>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-8 space-y-5">
          {whatsAppStep === 'phone' ? (
            <form onSubmit={handlePhoneSubmit} className="space-y-4">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  WhatsApp number
                </label>
                <div className="flex">
                  <CountryCodeSelector
                    selectedCountry={selectedCountry}
                    onChange={(country) => {
                      setSelectedCountry(country);
                      setPhone('');
                      setError('');
                    }}
                    disabled={phoneSubmitting}
                  />
                  <input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, maxLen);
                      setPhone(value);
                    }}
                    placeholder={selectedCountry.example}
                    autoComplete="tel"
                    className="flex-1 block w-full rounded-none rounded-r-lg border border-gray-300 px-3 py-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    disabled={phoneSubmitting}
                    required
                  />
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  We&apos;ll send a 6-digit OTP on WhatsApp. No password needed.
                </p>
              </div>

              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm leading-5 text-gray-700">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(event) => {
                    termsAcceptedRef.current = event.target.checked;
                    setTermsAccepted(event.target.checked);
                  }}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  required
                />
                <TermsMarketingConsentLabel />
              </label>

              {!termsAccepted && (
                <p role="alert" aria-live="polite" className="text-sm font-medium text-red-700">
                  {TERMS_ACCEPTANCE_REQUIRED_MESSAGE}
                </p>
              )}

              <button
                type="submit"
                disabled={phoneSubmitting || phone.length < 4 || !termsAccepted}
                className="w-full flex items-center justify-center py-3 px-4 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {phoneSubmitting ? <LoadingSpinner size="sm" className="text-white" /> : 'Send WhatsApp OTP'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleOtpSubmit} className="space-y-4">
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                  WhatsApp OTP
                </label>
                <input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtp(value);
                  }}
                  placeholder="123456"
                  autoComplete="one-time-code"
                  className="block w-full rounded-lg border border-gray-300 px-3 py-3 text-center text-2xl tracking-widest focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  disabled={phoneSubmitting}
                  required
                />
                <p className="mt-2 text-xs text-gray-500 text-center">
                  Code sent to {phoneDisplay}
                </p>
              </div>

              <button
                type="submit"
                disabled={phoneSubmitting || otp.length !== 6}
                className="w-full flex items-center justify-center py-3 px-4 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {phoneSubmitting ? <LoadingSpinner size="sm" className="text-white" /> : 'Verify and continue'}
              </button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={handleBackToPhone}
                  disabled={phoneSubmitting}
                  className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
                >
                  Change number
                </button>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={phoneSubmitting || resendCooldown > 0}
                  className="text-primary hover:text-primary-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
                </button>
              </div>
            </form>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-500">or</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={googleSubmitting || !termsAccepted}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {googleSubmitting ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
                  <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
                  <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
                  <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
                </svg>
                <span>Continue with Google Workspace Email</span>
              </>
            )}
          </button>
        </div>

        <div className="text-center text-sm text-gray-600">
          Need a Corporate workspace?{' '}
          <Link href="/request-access" className="text-primary font-medium hover:underline">
            Request access
          </Link>
        </div>
      </div>
    </div>
  );
}
